import { trpc } from '@/lib/trpc';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { AgentContextDocForm, AgentContextDocFormData } from './add';
import { parseTags } from './utils';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Flex } from '@televet/kibble-ui/chakra';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/agent-context-docs/$id')({
  component: EditAgentContextDoc,
  beforeLoad: () => ({
    pageTitle: 'Edit Context Document',
  }),
});

function EditAgentContextDoc() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const { data: doc, isLoading } = useQuery(
    trpc.agentContextDoc.findOne.queryOptions({ id })
  );

  const { mutate } = useMutation(
    trpc.agentContextDoc.updateOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Document updated',
          description: data.message,
        });
        navigate({ to: '/agent-context-docs' });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to update document',
          description: error.message || 'Failed to update document',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AgentContextDocFormData>({
    values: doc
      ? {
          provider: doc.provider,
          externalId: doc.externalId,
          title: doc.title,
          description: doc.description,
          tags: doc.tags.join(', '),
        }
      : undefined,
  });

  const onSubmit = (data: AgentContextDocFormData) => {
    mutate({
      id,
      data: {
        provider: data.provider,
        externalId: data.externalId,
        title: data.title,
        description: data.description,
        tags: parseTags(data.tags),
      },
    });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  return (
    <AgentContextDocForm
      onSubmit={onSubmit}
      onCancel={() => navigate({ to: '/agent-context-docs' })}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      watch={watch}
      setValue={setValue}
    />
  );
}
